import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

const AutoCacheClear = () => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [triggerReason, setTriggerReason] = useState<string>('');
  const { clearAuthCache, login } = useAuth();

  useEffect(() => {
    const handleLoadingStuck = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const duration = Math.floor(detail.duration / 1000);
      setTriggerReason(`Loading for ${duration} seconds - likely deployment detected`);
      setShowDialog(true);
    };

    window.addEventListener('auth-loading-stuck', handleLoadingStuck);

    return () => {
      window.removeEventListener('auth-loading-stuck', handleLoadingStuck);
    };
  }, []);

  const handleAutoClearAndReauth = async () => {
    setIsClearing(true);
    try {
      clearAuthCache();
      
      await new Promise<void>(resolve => setTimeout(resolve, 1000));
      
      await login();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error during auto cache clear and re-auth:', error);
      }
    } finally {
      setIsClearing(false);
      setShowDialog(false);
    }
  };

  const handleDismiss = () => {
    setShowDialog(false);
  };

  if (!showDialog) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Deployment Detected</h3>
        </div>
        
        <p className="text-gray-600 mb-4">
          {triggerReason}. This usually happens after backend updates.
        </p>
        
        <p className="text-gray-600 mb-6">
          We'll clear your authentication cache and sign you back in automatically.
        </p>
        
        <div className="flex space-x-3">
          <button
            onClick={handleAutoClearAndReauth}
            disabled={isClearing}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isClearing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Clearing & Re-authenticating...
              </>
            ) : (
              'Continue & Sign In'
            )}
          </button>
          
          <button
            onClick={handleDismiss}
            disabled={isClearing}
            className="px-4 py-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Wait Longer
          </button>
        </div>
        
        <p className="text-xs text-gray-500 mt-3">
          This will clear your local authentication tokens and require you to sign in again.
        </p>
      </div>
    </div>
  );
};

export default AutoCacheClear;
